import { useState, useEffect } from 'react';
import { X, Cookie, Settings } from 'lucide-react';
import { Button } from './ui/button';

// ═══════════════════════════════════════════
// COOKIE CONSENT BANNER
// ═══════════════════════════════════════════

export const CookieBanner = ({ onOpenCookiePolicy }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be disabled
    analytics: false,
  });

  useEffect(() => {
    // Check if consent was already given
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      try {
        const saved = JSON.parse(consent);
        setPreferences(saved);
        // Load analytics if consented
        if (saved.analytics) {
          loadAnalytics();
        }
      } catch (e) {
        // Invalid stored value, show banner
        setIsVisible(true);
      }
    }

    // Allow user to reopen the banner from anywhere (e.g. footer)
    const handleReopen = () => {
      setShowSettings(false);
      setIsVisible(true);
    };
    window.addEventListener('openCookieSettings', handleReopen);
    return () => window.removeEventListener('openCookieSettings', handleReopen);
  }, []);

  const loadAnalytics = () => {
    // Google Analytics loader (only if consented)
    // Note: Replace with your GA tracking ID
    if (typeof window !== 'undefined' && !window.gaLoaded) {
      window.gaLoaded = true;
      // Anonymize IP for privacy
      if (window.gtag) {
        window.gtag('config', 'GA_TRACKING_ID', {
          'anonymize_ip': true
        });
      }
    }
  };

  const handleAcceptAll = () => {
    const newPrefs = { essential: true, analytics: true };
    setPreferences(newPrefs);
    localStorage.setItem('cookieConsent', JSON.stringify(newPrefs));
    loadAnalytics();
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const newPrefs = { essential: true, analytics: false };
    setPreferences(newPrefs);
    localStorage.setItem('cookieConsent', JSON.stringify(newPrefs));
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    if (preferences.analytics) {
      loadAnalytics();
    }
    setIsVisible(false);
    setShowSettings(false);
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[9998] bg-white border-t border-[#E5E0DA] shadow-lg"
      role="dialog"
      aria-label="הסכמה לעוגיות"
      dir="rtl"
    >
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {!showSettings ? (
          // Main Banner
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="w-6 h-6 text-[#C4956A] flex-shrink-0 mt-1" />
              <div>
                <p className="text-[#2C2C2C] text-sm md:text-base">
                  אתר זה משתמש בעוגיות לשיפור חווית הגלישה.{' '}
                  <button 
                    onClick={onOpenCookiePolicy}
                    className="text-[#C4956A] hover:underline"
                  >
                    לפרטים ראו מדיניות עוגיות
                  </button>
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <Button
                onClick={handleAcceptAll}
                className="flex-1 md:flex-none rounded-full bg-[#C4956A] hover:bg-[#b38559] text-white"
                data-testid="cookie-accept"
              >
                אישור
              </Button>
              <Button
                onClick={handleRejectAll}
                variant="outline"
                className="flex-1 md:flex-none rounded-full border-[#2C2C2C] text-[#2C2C2C]"
                data-testid="cookie-reject"
              >
                דחייה
              </Button>
              <Button
                onClick={() => setShowSettings(true)}
                variant="ghost"
                className="flex-1 md:flex-none text-[#2C2C2C]/70"
                data-testid="cookie-settings"
              >
                <Settings className="w-4 h-4 ml-1" />
                הגדרות
              </Button>
            </div>
          </div>
        ) : (
          // Settings Panel
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-['Heebo'] font-bold text-lg text-[#2C2C2C]">הגדרות עוגיות</h3>
              <button onClick={() => setShowSettings(false)} className="text-[#2C2C2C]/50 hover:text-[#2C2C2C]">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-3">
              {/* Essential Cookies */}
              <div className="flex items-center justify-between p-3 bg-[#FAF7F2] rounded-lg">
                <div>
                  <p className="font-medium text-[#2C2C2C]">עוגיות הכרחיות</p>
                  <p className="text-sm text-[#2C2C2C]/60">נדרשות לתפעול תקין של האתר</p>
                </div>
                <input 
                  type="checkbox" 
                  checked={true} 
                  disabled 
                  className="w-5 h-5 accent-[#C4956A]"
                  aria-label="עוגיות הכרחיות - תמיד פעיל"
                />
              </div>
              
              {/* Analytics Cookies */}
              <div className="flex items-center justify-between p-3 bg-[#FAF7F2] rounded-lg">
                <div>
                  <p className="font-medium text-[#2C2C2C]">עוגיות ניתוח</p>
                  <p className="text-sm text-[#2C2C2C]/60">מסייעות לנו להבין כיצד הגולשים משתמשים באתר</p>
                </div>
                <input 
                  type="checkbox" 
                  checked={preferences.analytics}
                  onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                  className="w-5 h-5 accent-[#C4956A]"
                  aria-label="עוגיות ניתוח"
                />
              </div>
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button
                onClick={handleSavePreferences}
                className="rounded-full bg-[#C4956A] hover:bg-[#b38559] text-white"
              >
                שמירת העדפות
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
